import { useEffect, useState, useRef, useCallback } from "react";
import type { RefObject } from "react";
import { useTheme } from "@mui/material/styles";
import { isMock, isNonProd } from "../middleware/config";
import { getUser } from "../middleware/user";
import { watchInfo } from "../common/as-info";
import { onPageChange } from "./utils";

export const BUTTON_STATUSES = {
  VISIBLE: "VISIBLE",
  MINIMIZED: "MINIMIZED",
  LOADING: "LOADING",
  HIDDEN: "HIDDEN",
  ERROR: "ERROR",
} as const;

type ButtonStatus = typeof BUTTON_STATUSES[keyof typeof BUTTON_STATUSES];

export function isVisibleButton(buttonStatus: ButtonStatus): boolean {
  return buttonStatus !== BUTTON_STATUSES.HIDDEN;
}

interface ChatState {
  isLoaded: boolean;
  buttonStatus: ButtonStatus;
  handleOpenChat: () => void;
  chatLogout: () => void;
}


// @ts-ignore
export interface SalesforceWindow extends Window {
  embeddedservice_bootstrap?: {
    prechatAPI: {
      setVisiblePrechatFields: (
        fields: Record<string, { value: string }>,
      ) => void;
      setHiddenPrechatFields: (
        fields: Record<string, { value: string }>,
      ) => void;
    };
    utilAPI?: {
      launchChat: () => Promise<void>;
    };
  };
  initEmbeddedMessaging?: () => void;
  embeddedMessagingInitialized?: boolean;
  logoutAndClearMIAWSession?: () => void;
}



function getSalesforceUrls(env: string) {
  console.log("HelpWidget Environment:", env);
  if (env === "prod") {
    return {
      main: "https://wiley-corp.my.salesforce-sites.com/AmericasLiveAgent/resource/MIAW_UAXD",
      bootstrap: "https://wiley-corp.my.site.com/ESWWileyPLUSMessagingWe1719474783380/assets/js/bootstrap.min.js",
    };
  }
  if (env === "nonprod") {
    return {
      main: "https://wiley-corp--uat.sandbox.my.salesforce-sites.com/AmericasLiveAgent/resource/MIAW_UAXD",
      bootstrap: "https://wiley-corp--uat.sandbox.my.site.com/ESWWileyPLUSMessagingWe1719474783380/assets/js/bootstrap.min.js",
    };
  }

  return {
    main: "https://wiley-corp--servicedev.sandbox.my.salesforce-sites.com/AmericasLiveAgent/resource/MIAW_UAXD",
    bootstrap: "https://wiley-corp--servicedev.sandbox.my.site.com/ESWWileyPLUSMessagingWe1719474783380/assets/js/bootstrap.min.js",
  };
}

const SINGLETON_GLOBAL_STATE = {
  buttonStatus: BUTTON_STATUSES.HIDDEN as ButtonStatus,
};

function useHelpWidget(containerRef?: RefObject<HTMLDivElement>): ChatState {
  const theme = useTheme();
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [buttonStatus, setButtonStatus] = useState<ButtonStatus>(SINGLETON_GLOBAL_STATE.buttonStatus);

  const cleanupRef = useRef<(() => void) | null>(null);
  const salesforceWindow = window as unknown as SalesforceWindow;



  const user = getUser();


  useEffect(() => {
    SINGLETON_GLOBAL_STATE.buttonStatus = buttonStatus;
    console.log("HelpWidget Button Status:", buttonStatus);
  }, [buttonStatus]);

  const setVisiblePrechatFields = useCallback(() => {
    if (salesforceWindow.embeddedservice_bootstrap?.prechatAPI && user?.userId) {
      watchInfo(onPageChange);
      try {
        salesforceWindow.embeddedservice_bootstrap.prechatAPI.setVisiblePrechatFields({
          _firstName: { value: user.givenName || "" },
          _lastName: { value: user.familyName || "" },
          _email: { value: user.email || "" },
          ConnectID: { value: user.userId || "" },
        });
        console.log("✅ HelpWidget: User data configured");
      } catch (error) {
        console.error("❌ HelpWidget: Error setting user data:", error);
      }
    }
  }, [user, salesforceWindow.embeddedservice_bootstrap?.prechatAPI]);

  useEffect(() => {
    if (!containerRef?.current) {
      return;
    }

    const environment = () => {
      if (isMock()) {
        return "mock";
      }

      return isNonProd() ? "nonprod" : "prod";
    }

    const { main, bootstrap } = getSalesforceUrls(environment());

    const hideOriginalButton = () => {
      if (!document.getElementById("hide-original-styles")) {
        const style = document.createElement("style");
        style.id = "hide-original-styles";
        style.textContent = `
          .embeddedMessagingConversationButtonWrapper {
            display: none !important;
          }
          #embeddedMessagingFrame.isMinimized {
            display: none !important;
          }
          .embeddedMessagingFrame {
            margin: 0 5px !important;
          }
          .embeddedMessagingFrame.isMaximized {
            z-index: ${theme.customZIndex.helpWidgetMaximized} !important;
          }
        `;
        document.head.appendChild(style);
      }
    };

    hideOriginalButton();

    const loadSalesforceScripts = () => {
      if (!document.querySelector(`script[src="${main}"]`)) {
        console.log("📦 HelpWidget: Loading main script...");
        const script1 = document.createElement("script");
        script1.type = "text/javascript";
        script1.src = main;
        script1.async = true;
        document.body.appendChild(script1);
      }

      if (!document.querySelector(`script[src="${bootstrap}"]`)) {
        console.log("📦 HelpWidget: Loading bootstrap script...");
        const script2 = document.createElement("script");
        script2.type = "text/javascript";
        script2.src = bootstrap;
        script2.async = true;

        script2.onload = () => {
          console.log("✅ HelpWidget: Bootstrap script loaded");

          if (typeof salesforceWindow.initEmbeddedMessaging === "function") {
            if (!salesforceWindow.embeddedMessagingInitialized) {
              salesforceWindow.embeddedMessagingInitialized = true;
              salesforceWindow.initEmbeddedMessaging();
              setIsLoaded(true);
            } else {
              console.log("⚠️ HelpWidget: initEmbeddedMessaging already initialized, skipping duplicate call");
            }
          }

          const onButtonCreated = (event: any) => {
            if (event.detail?.buttonVisibility === "Show") {
              setButtonStatus((status: any) => (isVisibleButton(status) ? status : BUTTON_STATUSES.VISIBLE));
            }
          };

          const onConversationOpened = () => {
            setTimeout(() => {
              const chatFrame = document.querySelector("#embeddedMessagingFrame");
              if (chatFrame && !chatFrame.classList.contains("isMinimized")) {
                setButtonStatus(BUTTON_STATUSES.HIDDEN);
              }
            }, 100);
          };

          const onMinimize = () => {
            setButtonStatus(BUTTON_STATUSES.MINIMIZED);
          };

          const onCloseChat = () => {
            setButtonStatus(BUTTON_STATUSES.VISIBLE);
          };

          window.addEventListener("onEmbeddedMessagingReady", setVisiblePrechatFields);
          window.addEventListener("onEmbeddedMessagingButtonCreated", onButtonCreated);
          window.addEventListener("onEmbeddedMessagingConversationOpened", onConversationOpened);
          window.addEventListener("onEmbeddedMessagingWindowMinimized", onMinimize);
          window.addEventListener("onEmbeddedMessagingWindowClosed", onCloseChat);

          cleanupRef.current = () => {
            window.removeEventListener("onEmbeddedMessagingReady", setVisiblePrechatFields);
            window.removeEventListener("onEmbeddedMessagingButtonCreated", onButtonCreated);
            window.removeEventListener("onEmbeddedMessagingConversationOpened", onConversationOpened);
            window.removeEventListener("onEmbeddedMessagingWindowMinimized", onMinimize);
            window.removeEventListener("onEmbeddedMessagingWindowClosed", onCloseChat);
          };
        };
        document.body.appendChild(script2);
      }
    };

    loadSalesforceScripts();

    return () => {
      console.log("🧹 HelpWidget: Cleaning up...");
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }

      const script1 = document.querySelector(`script[src="${main}"]`);
      const script2 = document.querySelector(`script[src="${bootstrap}"]`);
      if (script1) script1.remove();
      if (script2) script2.remove();


      if (salesforceWindow.embeddedMessagingInitialized) {
        salesforceWindow.embeddedMessagingInitialized = false;
      }
    };
  }, [containerRef, setVisiblePrechatFields, theme.customZIndex.helpWidgetMaximized]);


  const chatLogout = useCallback(() => {
    if (isLoaded) {
      salesforceWindow.logoutAndClearMIAWSession();
      console.log("✅ HelpWidget: Chat logout successful");
    }
  }, [isLoaded, salesforceWindow]);

  const handleOpenChat = useCallback(async () => {
    setVisiblePrechatFields();
    setButtonStatus(BUTTON_STATUSES.LOADING);

    try {
      await salesforceWindow.embeddedservice_bootstrap?.utilAPI?.launchChat();
      setButtonStatus(BUTTON_STATUSES.HIDDEN);
    } catch (error) {
      console.error("❌ HelpWidget: Error launching chat:", error);
      setButtonStatus(BUTTON_STATUSES.ERROR);
    }
  }, [setVisiblePrechatFields, salesforceWindow.embeddedservice_bootstrap?.utilAPI]);

  return { isLoaded, buttonStatus, handleOpenChat, chatLogout };
}

export default useHelpWidget;
